import type { PayloadAction } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { fetchLiveSupportMessagesAction } from '../../apiActions/fetchLiveSupportMessagesAction';
import { fetchTableLevelMessagesAction } from '../../apiActions/fetchTableLevelMessagesAction';

import type { ChatState, Message, ChatSubscriptionState } from './types';
import { ChatBlockType } from './types';

const MAX_MESSAGE_COUNT = 100;

export const gameChatMessagesAdapter = createEntityAdapter<Message, Message['messageId']>({
  selectId: (message) => message.messageId,
  sortComparer: (a, b) => b.timestamp - a.timestamp,
});

export const liveSupportChatMessagesAdapter = createEntityAdapter<Message, Message['messageId']>({
  selectId: (message) => message.messageId,
  sortComparer: (a, b) => b.timestamp - a.timestamp,
});

const initialState: ChatState = {
  chatBlockType: ChatBlockType.LowBalance,
  chatSubscriptionState: 'UNSUBSCRIBED',
  gameChatMessages: gameChatMessagesAdapter.getInitialState(),
  isBetChatAllowed: true,
  isGameChatBlocked: false,
  isGameChatHistoryFetched: false,
  isLiveSupportChatBlocked: false,
  isLiveSupportChatRead: true,
  lastChatMessage: null,
  liveSupportChatMessages: liveSupportChatMessagesAdapter.getInitialState(),
  miniChatMessages: [],
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    storeGameChatMessage: (state, action: PayloadAction<Message>) => {
      gameChatMessagesAdapter.addOne(state.gameChatMessages, action.payload);

      const allMessages = gameChatMessagesAdapter.getSelectors().selectAll(state.gameChatMessages);
      if (allMessages.length > MAX_MESSAGE_COUNT) {
        const oldestMessageId = allMessages[allMessages.length - 1]?.messageId;
        // Remove oldest message
        if (oldestMessageId) {
          gameChatMessagesAdapter.removeOne(state.gameChatMessages, oldestMessageId);
        }
      }
    },
    storeLiveSupportMessage: (state, action: PayloadAction<Message>) => {
      liveSupportChatMessagesAdapter.addOne(state.liveSupportChatMessages, action.payload);
    },
    clearLiveSupportMessages: (state) => {
      liveSupportChatMessagesAdapter.removeAll(state.liveSupportChatMessages);
    },
    storeLastChatMessage: (state, action: PayloadAction<Message>) => {
      state.lastChatMessage = action.payload;
    },
    removeGameChatMessage: (state, action: PayloadAction<string>) => {
      gameChatMessagesAdapter.removeOne(state.gameChatMessages, action.payload);
    },
    setLiveSupportReadStatus: (state, action: PayloadAction<boolean>) => {
      state.isLiveSupportChatRead = action.payload;
    },
    setChatSubscriptionState: (state, action: PayloadAction<ChatSubscriptionState>) => {
      state.chatSubscriptionState = action.payload;
    },
    setChatBlockType: (state, action: PayloadAction<ChatBlockType>) => {
      state.chatBlockType = action.payload;
    },
    setIsBetChatAllowed: (state, action: PayloadAction<boolean>) => {
      state.isBetChatAllowed = action.payload;
    },
    setIsGameChatBlocked: (state, action: PayloadAction<boolean>) => {
      state.isGameChatBlocked = action.payload;
    },
    setIsLiveSupportChatBlocked: (state, action: PayloadAction<boolean>) => {
      state.isLiveSupportChatBlocked = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLiveSupportMessagesAction.fulfilled, (state, action) => {
        liveSupportChatMessagesAdapter.addMany(state.liveSupportChatMessages, action.payload);
      })
      .addCase(fetchTableLevelMessagesAction.fulfilled, (state, action) => {
        gameChatMessagesAdapter.addMany(state.gameChatMessages, action.payload);
      })
      .addCase(fetchTableLevelMessagesAction.pending, (state) => {
        state.isGameChatHistoryFetched = true;
      });
  },
});

export const chatActions = chatSlice.actions;
